import { useState } from 'react';
import { Plus, Edit, Copy, Trash2, Search, FolderKanban } from 'lucide-react';
import { MilestoneSetsEducationalNotice } from './MilestoneSetsEducationalNotice';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useMilestoneSets } from '@/hooks/useMilestoneSets';
import { MilestoneSetDialog } from './MilestoneSetDialog';
import { DeleteMilestoneSetDialog } from './DeleteMilestoneSetDialog';
import { format } from 'date-fns';

interface MilestoneSetsLibraryProps {
  isAdminView?: boolean;
}

export function MilestoneSetsLibrary({ isAdminView = false }: MilestoneSetsLibraryProps) {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSet, setEditingSet] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'view' | 'edit' | 'create'>('create');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [setToDelete, setSetToDelete] = useState<any>(null);

  const { milestoneSets, loading, cloneMilestoneSet, refetch } = useMilestoneSets({
    category: categoryFilter && categoryFilter !== 'all' ? categoryFilter : undefined,
    search: search || undefined,
  });

  const categories = [
    { value: 'print_materials', label: 'Print Materials' },
    { value: 'packaging', label: 'Packaging' },
    { value: 'signage', label: 'Signage' },
    { value: 'merchandising', label: 'Merchandising' },
    { value: 'other', label: 'Other' },
  ];

  const handleClone = async (set: any) => {
    const newName = `${set.name} (Copy)`;
    await cloneMilestoneSet(set.id, newName);
  };

  const handleRowClick = (set: any) => {
    setEditingSet(set);
    setViewMode('view');
    setDialogOpen(true);
  };

  const handleEdit = (set: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSet(set);
    setViewMode('edit');
    setDialogOpen(true);
  };

  const handleDelete = (set: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSetToDelete(set);
    setDeleteDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingSet(null);
    setViewMode('create');
    setDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      refetch();
    }
  };

  const sortedSets = [...milestoneSets].sort((a, b) => {
    if (sortBy === 'name') {
      return a.name.localeCompare(b.name);
    } else if (sortBy === 'created_at') {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    } else if (sortBy === 'usage_count') {
      return (b.usage_count || 0) - (a.usage_count || 0);
    } else if (sortBy === 'milestone_count') {
      return (b.milestone_template_items?.length || 0) - (a.milestone_template_items?.length || 0);
    }
    return 0;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Milestone Sets</h1>
          <p className="text-muted-foreground mt-1">Create and manage reusable milestone sets for your jobs</p>
        </div>
        <Button onClick={handleCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          Create New Milestone Set
        </Button>
      </div>

      <MilestoneSetsEducationalNotice />

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search milestone sets..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v as any)}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
              <SelectTrigger>
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">Date Created</SelectItem>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="usage_count">Usage Count</SelectItem>
                <SelectItem value="milestone_count">Milestone Count</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent className="py-12">
            <div className="flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          </CardContent>
        </Card>
      ) : sortedSets.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center">
                  <FolderKanban className="h-8 w-8 text-muted-foreground" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">No milestone sets found</h3>
                <p className="text-muted-foreground mt-1">
                  {search || categoryFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Create your first milestone set to get started'}
                </p>
              </div>
              {!search && categoryFilter === 'all' && (
                <Button onClick={handleCreate} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Create Your First Milestone Set
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Milestones</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Usage Count</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedSets.map((set) => (
                  <TableRow
                    key={set.id}
                    className="cursor-pointer hover:bg-secondary/50"
                    onClick={() => handleRowClick(set)}
                  >
                    <TableCell className="font-medium">
                      {set.name}
                      {(set as any).is_standard && (
                        <Badge variant="default" className="ml-2">Standard</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {categories.find(c => c.value === set.category)?.label || set.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {set.milestone_template_items?.length || 0}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(set.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {set.usage_count || 0}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleEdit(set, e)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClone(set);
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleDelete(set, e)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <MilestoneSetDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        editingSet={editingSet}
        mode={viewMode}
        onModeChange={setViewMode}
        isAdminView={isAdminView}
      />

      {/* Delete Dialog */}
      <DeleteMilestoneSetDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        milestoneSet={setToDelete}
      />
    </div>
  );
}
